<template>
  <div>
    <q-inner-loading
      style="z-index: 5;"
      :showing="showingData.loading && !reloading"
    />
    <div
      ref="wrapper"
      class="wrapper"
      :class="fitHeight ? 'absolute-full' : ''"
      :style="{
        zoom: viewOptions.zoom,
        height: fitHeight ? null : (height + 'px'),
        'min-height': forceHeight,
        'max-height': forceHeight
      }"
      @scroll="computeWrapperScrollLeft"
    >
      <div
        ref="schedule"
        class="schedule"
        data-test-id="schedule-view-schedule"
        :style="{
          width: scheduleWidth + 'px',
          height: (height - 40) + 'px',
        }"
      >
        <div
          v-for="timeSlot in viewOptions.showTimeSlotHeader ? computedTimeSlots : []"
          :key="timeSlot.id"
          :style="{
            top: timeSlot.top + 'px',
            left: timeSlot.left + 'px',
            width: timeSlot.width + 'px'
          }"
          class="text-center text-caption time-slot text-no-wrap"
          style="line-height: 28px;"
        >
          <div
            class="target-time"
            :style="{ left: (timeSlot.targetTimeOffset - 2) + 'px', }"
          >
            |
          </div>
          {{ timeSlot.name }}
        </div>

        <div
          v-for="hour in viewOptions.showTimeHeader ? hours : []"
          :key="hour.left"
          :style="{
            top: hour.top,
            left: hour.left,
            width: hour.width
          }"
          class="text-center text-caption label overflow-hidden text-no-wrap"
          style="line-height: 28px;"
        >
          {{ hour.label }}
        </div>

        <div
          v-for="hourGridBox in hourGridBoxes"
          :key="'hour-grid-box-' + hourGridBox.left"
          :style="hourGridBox"
          class="hour-grid-box"
        />

        <div
          v-for="screenGridBox in screenGridBoxes"
          :key="'screen-grid-box-' + screenGridBox.top"
          :style="screenGridBox"
          class="screen-grid-box"
        />

        <div
          v-for="screenLabel in viewOptions.showScreenLabels ? screenLabels : []"
          :key="'screen-label-' + screenLabel.top"
          :style="screenLabel"
          class="screen-label"
        >
          {{ screenLabel.label }}
        </div>

        <div
          v-if="isToday && viewOptions.showCurrentTime"
          :style="todayLine"
          class="today-line"
        />
        <showing-slot
          v-for="showing in showings"
          :key="showing.id"
          :showing="showing"
          :view-options="viewOptions"
          :is-today="isToday"
          :starting-y="headerHeight"
          :total-item-height="totalItemHeight"
          :current-minutes="currentMinutes"
          :pixels-per-minute="pixelsPerMinute"
          :is-now-near-showtimes="isNowNearShowtimes"
          :screens="filteredScreens"
          @showing-slot-created="handleShowingSlotCreated"
          @showing-slot-destroyed="handleShowingSlotDestroyed"
          @select-showing="showing => selectShowing(showing)"
          @select-showing-detail="showing => selectShowingDetail(showing)"
        />
      </div>
    </div>
    <schedule-menu
      :view-options="viewOptions"
      :style="{
        bottom: horizontalScrollbarHeight + 'px',
        right: verticalScrollbarWidth + 'px'
      }"
      :is-dirty="isDirty"
      :can-save="canSave"
      :save-label="saveLabel"
      @save="save"
      @revert="revert"
    />
    <select-marathon-showing-dialog
      ref="selectMarathonShowingDialog"
      @select-marathon-showing="showing => selectShowing(showing, null, true)"
    />
  </div>
</template>

<script>
import { date } from 'quasar'
import { minutesPerHour } from 'src/models/schedule/ShowingSlot'
import { START_HOUR, timeToMinutes } from 'src/models/schedule/TimeSlot'
import ScheduleMenu from 'src/modules/admin/report/ScheduleMenu'
import ShowingSlot from 'src/modules/admin/report/ShowingSlot'
import SelectMarathonShowingDialog from 'src/modules/register/dialogs/SelectMarathonShowingDialog'

export default {
  name: 'ScheduleView',
  components: {
    ShowingSlot,
    ScheduleMenu,
    SelectMarathonShowingDialog
  },
  props: {
    shown: {
      type: Boolean,
      required: false,
      default: true
    },
    fitHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    date: {
      type: String,
      required: false,
      default: null
    },
    saveLabel: {
      type: String,
      required: false,
      default: 'Save'
    },
    canSave: {
      type: Boolean,
      required: false,
      default: false
    },

    screenIds: {
      type: Array,
      required: false,
      default: null
    },
    showTimeHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    showTimeSlotHeader: {
      type: Boolean,
      required: false,
      default: false
    },
    showScreenLabels: {
      type: Boolean,
      required: false,
      default: true
    },
    showTimes: {
      type: Boolean,
      required: false,
      default: false
    },
    showTimeChips: {
      type: Boolean,
      required: false,
      default: false
    },
    showRating: {
      type: Boolean,
      required: false,
      default: true
    },
    showSeatChart: {
      type: Boolean,
      required: false,
      default: false
    },
    showPriceCard: {
      type: Boolean,
      required: false,
      default: false
    },
    showShowingBadges: {
      type: Boolean,
      required: false,
      default: false
    },
    showPosters: {
      type: Boolean,
      required: false,
      default: false
    },
    showProgressBar: {
      type: Boolean,
      required: false,
      default: false
    },
    highlightStartingShowings: {
      type: Boolean,
      required: false,
      default: true
    },
    highlightEndingShowings: {
      type: Boolean,
      required: false,
      default: true
    },
    highlightCurrentShowings: {
      type: Boolean,
      required: false,
      default: true
    },
    showRelativeTimes: {
      type: Boolean,
      required: false,
      default: true
    },
    showRelativeTimeChips: {
      type: Boolean,
      required: false,
      default: false
    },
    showCurrentTime: {
      type: Boolean,
      required: false,
      default: true
    },
    colorByOccupancy: {
      type: Boolean,
      required: false,
      default: false
    },
    hideDistanceShowings: {
      type: Boolean,
      required: false,
      default: true
    },
    distantShowingThresholdHours: {
      type: Number,
      required: false,
      default: 3.5
    },
    zoom: {
      type: Number,
      required: false,
      default: 1
    },
    forceHeight: {
      type: String,
      required: false,
      default: null
    },
    showingsQuery: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['select-showing', 'edit-held-seats', 'save'],
  data () {
    return {
      today: this.currentSite.today,

      reloading: false,

      horizontalScrollbarHeight: 0,
      verticalScrollbarWidth: 0,
      wrapperScrollLeft: 0,

      internalDate: this.date,

      viewOptions: {
        screenIds: this.screenIds,
        showTimeHeader: this.showTimeHeader,
        showTimeSlotHeader: this.showTimeSlotHeader,
        showScreenLabels: this.showScreenLabels,
        showTimes: this.showTimes,
        showTimeChips: this.showTimeChips,
        showRating: this.showRating,
        showSeatChart: this.showSeatChart,
        showPriceCard: this.showPriceCard,
        showShowingBadges: this.showShowingBadges,
        showPosters: this.showPosters,
        showProgressBar: this.showProgressBar,
        highlightStartingShowings: this.highlightStartingShowings,
        highlightEndingShowings: this.highlightEndingShowings,
        highlightCurrentShowings: this.highlightCurrentShowings,
        colorByOccupancy: this.colorByOccupancy,
        showRelativeTimes: this.showRelativeTimes,
        showRelativeTimeChips: this.showRelativeTimeChips,
        showCurrentTime: this.showCurrentTime,
        hideDistanceShowings: this.hideDistanceShowings,
        distantShowingThresholdHours: this.distantShowingThresholdHours,
        zoom: this.zoom
      },

      displayMode: 'poster',

      contextMenuShown: false,

      pixelsPerMinute: 2,

      showingSlots: []
    }
  },
  configuration: ['currentCircuit', 'currentSite', 'screens', 'timeSlots'],
  computed: {
    showingData () {
      return this.showingsQuery || this.showingsForDate
    },
    showings () {
      return _.filter(this.showingData.data, showing =>
        _.includes(_.map(this.filteredScreens, 'id'), showing.screen.id) && !showing.hasMarathon
      )
    },
    isToday () {
      return this.currentSite.today === this.internalDate
    },
    isNowNearShowtimes () {
      const currentBeforeFirstShowing = this.currentMinutes < this.minMinutes - 60
      const currentAfterLastShowing = this.currentMinutes > this.maxMinutes + 60

      return !currentBeforeFirstShowing && !currentAfterLastShowing
    },
    currentMinutes () {
      return timeToMinutes(this.currentSite.siteTime)
    },
    minMinutes () {
      return _(this.showingSlots).map('startTimeMinutes').min()
    },
    maxMinutes () {
      return _(this.showingSlots).map('endTimeMinutes').max()
    },
    totalItemHeight () {
      return 70 + (this.viewOptions.showTimes ? 15 : 0)
    },
    headerHeight () {
      return (this.viewOptions.showTimeSlotHeader ? 30 : 0) + (this.viewOptions.showTimeHeader ? 30 : 0)
    },
    height () {
      return this.headerHeight + this.filteredScreens.length * this.totalItemHeight + this.horizontalScrollbarHeight
    },
    filteredScreens () {
      return this.viewOptions.screenIds ? _.filter(this.screens.data, screen => _.includes(this.viewOptions.screenIds, screen.id)) : this.screens.data
    },
    scheduleWidth () {
      return 24 * minutesPerHour * this.pixelsPerMinute
    },
    computedTimeSlots () {
      const timeSlots = []
      const top = 0
      _.each(this.timeSlots.data, (timeSlot) => {
        const startTimeMinutesOffset = this.timeInPixels(timeSlot.startTime)
        const endTimeMinutesOffset = this.timeInPixels(timeSlot.endTime)
        const targetTimeMinutesOffset = this.timeInPixels(timeSlot.targetTime)

        timeSlots.push({
          id: timeSlot.id,
          name: timeSlot.name,
          top,
          left: startTimeMinutesOffset - 1,
          width: endTimeMinutesOffset - startTimeMinutesOffset + this.pixelsPerMinute - 1,
          targetTimeOffset: targetTimeMinutesOffset - startTimeMinutesOffset
        })
      })

      return timeSlots
    },
    hours () {
      const hours = []
      for (let hourCount = 0; hourCount < 24; hourCount += 1) {
        const time = date.buildDate({ hours: START_HOUR + hourCount, minutes: 0 })
        hours.push({
          top: (this.viewOptions.showTimeSlotHeader ? 30 : 0) + 'px',
          left: (hourCount * minutesPerHour * this.pixelsPerMinute) + 'px',
          width: (minutesPerHour * this.pixelsPerMinute) + 'px',
          label: date.formatDate(time, this.pixelsPerMinute < 2 ? 'h a' : 'h:mm a')
        })
      }

      return hours
    },
    hourGridBoxes () {
      const gridBoxes = []
      for (let hourCount = 0; hourCount < 24; hourCount += 1) {
        gridBoxes.push({
          top: this.headerHeight + 'px',
          left: (hourCount * minutesPerHour * this.pixelsPerMinute) + 'px',
          width: (minutesPerHour * this.pixelsPerMinute) + 'px',
          height: `calc(100% - ${this.headerHeight}px)`,
          'z-index': 1
        })
      }

      return gridBoxes
    },
    todayLine () {
      return {
        left: this.timeInPixels(this.currentSite.siteTime) + 'px'
      }
    },
    screenGridBoxes () {
      const gridBoxes = []
      const screenHeight = this.totalItemHeight
      _.each(this.filteredScreens, (screen, screenIndex) => {
        gridBoxes.push({
          screenId: screen.id,
          top: this.headerHeight + (screenHeight * screenIndex) + 'px',
          height: this.totalItemHeight + 'px',
          'background-color': screenIndex % 2 ? '#99999955' : '#99999933',
          border: 'solid 1px #CCC',
          'z-index': 2
        })
      })

      return gridBoxes
    },
    screenLabels () {
      const screenLabels = []
      const screenHeight = this.totalItemHeight
      _.each(this.filteredScreens, (screen, screenIndex) => {
        screenLabels.push({
          screenId: screen.id,
          top: (this.viewOptions.showTimes ? 5 : 0) + this.headerHeight + (screenHeight * screenIndex) + 'px',
          left: 10 + this.wrapperScrollLeft + 'px',
          color: screenIndex % 2 ? '#00000033' : '#00000022',
          label: screen.displayOrder
        })
      })

      return screenLabels
    },
    isDirty () {
      return !_.isEqual(this.viewOptions, this.originalViewOptions)
    }
  },
  watch: {
    showings () {
      this.autoFit()
    },
    'showingData.data' () {
      this.autoFit()
    },
    'viewOptions.hideDistanceShowings' () {
      this.autoFit()
    },
    'viewOptions.distantShowingThresholdHours' () {
      this.autoFit()
    },
    'viewOptions.zoom' () {
      this.autoFit()
    },
    date () {
      this.internalDate = this.date
    },
    async 'pixelsPerMinute' () {
      await this.$nextTick()
      this.computeHorizontalScrollbarHeight()
    },
    async shown () {
      if (!this.shown) return

      await this.$nextTick()
      this.autoFit()
    }
  },
  async created () {
    window.addEventListener('resize', this.autoFit)

    if (!this.showingsQuery) this.fetchShowings()

    if (!this.showingsQuery) this.checkForRelaunch()

    this.originalViewOptions = _.clone(this.viewOptions)

    // Make input changes reactive
    _.each(_.keys(this.viewOptions), field => {
      this.$watch(field, (newValue) => {
        this.viewOptions[field] = newValue
      })
    })
  },
  async mounted () {
    await this.$nextTick()
    this.autoFit()
  },
  unmounted () {
    window.removeEventListener('resize', this.autoFit)

    this.isDestroyed = true

    if (this.fetchShowingsTimeout) {
      clearTimeout(this.fetchShowingsTimeout)
    }
    if (this.checkForRelaunchTimeout) {
      clearTimeout(this.checkForRelaunchTimeout)
    }
  },
  methods: {
    computeHorizontalScrollbarHeight () {
      if (!this.$refs.wrapper) {
        this.horizontalScrollbarHeight = 0
        return
      }

      this.horizontalScrollbarHeight = this.$refs.wrapper.offsetHeight - this.$refs.wrapper.clientHeight
    },
    computeVerticalScrollbarWidth () {
      if (!this.$refs.wrapper) {
        this.verticalScrollbarWidth = 0
        return
      }

      this.verticalScrollbarWidth = this.$refs.wrapper.offsetWidth - this.$refs.wrapper.clientWidth
    },
    computeWrapperScrollLeft () {
      this.wrapperScrollLeft = this.$refs.wrapper?.scrollLeft || 0
    },
    async autoFit () {
      if (!this.shown) return

      await this.$nextTick()

      if (!this.$refs.wrapper || !this.$refs.wrapper.clientWidth) return

      const originalMinMinutes = this.minMinutes
      let minMinutes = originalMinMinutes
      let maxMinutes = this.maxMinutes

      if (!minMinutes || !maxMinutes) return

      if (this.viewOptions.hideDistanceShowings && this.isToday) {
        if (this.isNowNearShowtimes) {
          // Find the min and max that represents zooming near the current time
          const minThrehold = this.currentMinutes - this.viewOptions.distantShowingThresholdHours * 60
          const maxThrehold = this.currentMinutes + this.viewOptions.distantShowingThresholdHours * 60

          if (minMinutes < minThrehold) {
            minMinutes = minThrehold
          }

          if (minMinutes > maxMinutes - 2 * this.viewOptions.distantShowingThresholdHours * 60) {
            minMinutes = maxMinutes - 2 * this.viewOptions.distantShowingThresholdHours * 60
          }

          if (maxMinutes > maxThrehold) {
            maxMinutes = maxThrehold
          }
        }
      }

      const xMargin = 30
      let pixelsPerMinute = Math.round((this.$refs.wrapper.clientWidth - xMargin) / (maxMinutes - minMinutes) * 100) / 100
      pixelsPerMinute = Math.min(pixelsPerMinute, 10.0)
      pixelsPerMinute = Math.max(pixelsPerMinute, 0.25)
      this.pixelsPerMinute = pixelsPerMinute

      await this.$nextTick()
      const minX = _(this.showingSlots).map('left').min()

      this.$refs.wrapper.scroll({
        left: minX + this.pixelsPerMinute * (minMinutes - originalMinMinutes) - xMargin / 2,
        top: 0
      })

      this.computeVerticalScrollbarWidth()
    },
    fetchShowings () {
      // Set timeout instead of set interval, prevents call
      // stacking if fetch takes time
      this.fetchShowingsTimeout = setTimeout(async () => {
        if (this.isDestroyed || this.showingsQuery) return

        if (this.showingsForDate.loading) return

        this.validateToday()

        this.reloading = true
        await this.showingsForDate.fetch(true)
        this.reloading = false

        // Call recursively
        this.fetchShowings()
      }, 20 * 1000)
    },
    validateToday () {
      if (this.today === this.currentSite.today) return

      const oldToday = this.today
      this.today = this.currentSite.today
      this.tomorrow = this.currentSite.tomorrow

      if (this.internalDate === oldToday) {
        this.internalDate = this.today
      }
    },
    timeInPixels (time) {
      return timeToMinutes(time) * this.pixelsPerMinute
    },
    checkForRelaunch () {
      let lastFired = new Date().getTime()
      this.checkForRelaunchTimeout = setTimeout(async () => {
        if (this.isDestroyed || this.showingsQuery) return

        const now = new Date().getTime()
        // if it's been more than 5 minutes since we lauched page
        if (now - lastFired > 1000 * 60 * 5) {
          this.validateToday()
          this.reloading = true
          await this.showingsForDate.fetch(true)
          this.reloading = false
        }
        lastFired = now
        this.checkForRelaunch()
      }, 500)
    },
    handleShowingSlotCreated (showingSlot) {
      this.showingSlots.push(showingSlot)
    },
    handleShowingSlotDestroyed (showingSlot) {
      const index = this.showingSlots.indexOf(showingSlot)
      if (index > -1) {
        this.showingSlots.splice(index, 1)
      }
    },
    selectShowing (showing, showingSlot, force = false) {
      if (this.$register) {
        if (!force && showing.isMarathon && showing.availableMarathonShowings.length) {
          this.$refs.selectMarathonShowingDialog.show(showing)
          return
        }

        this.$emit('select-showing', showing, this.showingSlotForShowing(showing))
      } else if (this.$admin) {
        this.$emit('edit-held-seats', showing)
      }
    },
    selectShowingDetail (showing) {
      if (this.$register) {
        this.$emit('edit-held-seats', showing)
      } else if (this.$admin) {
        this.$editModel(showing)
      }
    },
    showingSlotForShowing (showing) {
      const showingSlot = _.find(this.showingSlots, showingSlot => showingSlot.showing.id === showing.id)

      const boundingRectangle = showingSlot?.$refs?.card?.$el?.getBoundingClientRect()

      if (!boundingRectangle) return

      return {
        top: boundingRectangle.top,
        left: boundingRectangle.left,
        width: boundingRectangle.width
      }
    },
    save () {
      this.originalViewOptions = _.clone(this.viewOptions)
      this.$emit('save', this.viewOptions)
    },
    revert () {
      this.viewOptions = _.clone(this.originalViewOptions)
    }
  },
  models: {
    showingsForDate: {
      movie: 'default',
      showing: {
        movie: 'default',
        screen: 'default',
        seatsRemaining: true,
        seatsRemainingWithoutSocialDistancing: true
      },
      showings: {
        movie: 'default',
        screen: 'default',
        seatsRemaining: true,
        seatsRemainingWithoutSocialDistancing: true
      },
      screen: 'default',
      showingBadges: 'default',
      priceCard: 'default',
      predictedAttendance: true,
      seatsRemaining: true,
      seatsRemainingWithoutSocialDistancing: true,
      titleClass: 'default',
      variables () {
        return {
          date: this.internalDate,
          titleClassIds: this.initialTitleClassIds ? this.initialTitleClassIds : null,
          skip: !this.internalDate || !_.isString(this.internalDate) || this.showingsQuery
        }
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .wrapper {
    overflow: auto;
    border: solid 1px #AAA;
    border-radius: 0;
    background-color: #EEE;
  }

  .schedule {
    min-height: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    background-color: #FFF;
  }

  .label {
    height: 30px;
    position: absolute;
    border: solid 1px #AAA;
  }

  .time-slot {
    border: solid 1px #AAA;
    position: absolute;
    height: 30px;
    overflow: hidden;
  }

  .target-time {
    position: absolute;
    top: 10px;
  }

  .hour-grid-box {
    border: dashed 1px #CCC;
    position: absolute;
  }

  .day-grid-box {
    position: absolute;
    width: 100%;
  }

  .screen-grid-box {
    position: absolute;
    width: 100%;
  }

  .screen-label {
    position: absolute;
    font-size: 45px;
    z-index: 0;
  }

  .showing-demo {
    position: relative;
    padding: 10px;
    border: solid 2px;
  }

  .today-line {
    border-left: dashed 2px #C00C;
    position: absolute;
    top: 0;
    height: 100%;
    width: 0px;
  }
</style>
